import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DISPOSABLE_DOMAINS = [
  'mailinator.com', 'yopmail.com', 'tempmail.com', 'guerrillamail.com',
  'throwam.com', 'sharklasers.com', 'guerrillamailblock.com', 'grr.la',
  'guerrillamail.info', 'spam4.me', 'trashmail.com', 'trashmail.me',
  'dispostable.com', 'maildrop.cc', 'spamgourmet.com', 'spamgourmet.net',
  'tempinbox.com', 'fakeinbox.com', 'mailnull.com', 'spamex.com',
  'throwam.com', 'discard.email', 'spamfree24.org', 'mailexpire.com',
  '10minutemail.com', 'minutemail.com', 'tempmail.net', 'getairmail.com',
  'filzmail.com', 'spamhereplease.com', 'mailseal.de', 'spamhole.com',
];

const CONTENT = {
  FR: {
    title: 'Téléchargez votre composite sans watermark',
    sub: 'Entrez votre email pour télécharger votre composite HD gratuit et sans filigrane. Vous rejoignez la communauté Compify.',
    placeholder: 'votre@email.com',
    btn: 'TÉLÉCHARGER SANS WATERMARK',
    loading: 'GÉNÉRATION...',
    noRegister: 'Vous ne souhaitez pas vous inscrire ?',
    withWatermark: 'Télécharger avec watermark compify.fr',
    legal: 'En vous inscrivant vous acceptez de recevoir nos conseils pour mannequins et comédiens. Désabonnement en 1 clic.',
    errorDisposable: 'Les adresses email temporaires ne sont pas acceptées.',
    errorInvalid: 'Veuillez entrer une adresse email valide.',
  },
  EN: {
    title: 'Download your composite without watermark',
    sub: 'Enter your email to download your free HD composite without watermark. Join the Compify community.',
    placeholder: 'your@email.com',
    btn: 'DOWNLOAD WITHOUT WATERMARK',
    loading: 'GENERATING...',
    noRegister: 'Don\'t want to sign up?',
    withWatermark: 'Download with compify.fr watermark',
    legal: 'By signing up you agree to receive our tips for models and actors. Unsubscribe in 1 click.',
    errorDisposable: 'Disposable email addresses are not accepted.',
    errorInvalid: 'Please enter a valid email address.',
  }
};

export default function EmailPopup({ onSuccess, onSkip, onClose, siteLang = 'FR' }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const t = CONTENT[siteLang] || CONTENT.FR;

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  };

  const isDisposable = (email) => {
    const domain = email.trim().split('@')[1]?.toLowerCase().replace(/\.$/, '');
    return DISPOSABLE_DOMAINS.includes(domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValidEmail(email)) {
      setError(t.errorInvalid);
      return;
    }

    if (isDisposable(email)) {
      setError(t.errorDisposable);
      return;
    }

    setLoading(true);
    try {
      await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      console.error('Brevo error:', err);
    }
    localStorage.setItem('compify-user-email', email);
    onSuccess();
    setLoading(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '24px'
        }}>
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          style={{
            backgroundColor: '#0e0e0e', padding: '48px 40px',
            maxWidth: '440px', width: '100%', position: 'relative'
          }}>

          <button onClick={onClose} style={{
            position: 'absolute', top: '16px', right: '16px',
            backgroundColor: 'transparent', border: 'none',
            color: '#555', fontSize: '20px', cursor: 'pointer', lineHeight: 1
          }}>×</button>

          <p style={{ color: '#FF0066', fontSize: '10px', letterSpacing: '4px', fontWeight: '700', margin: '0 0 12px' }}>COMPIFY</p>
          <h2 style={{ color: '#fff', fontSize: '22px', fontWeight: '900', margin: '0 0 12px', letterSpacing: '1px', lineHeight: 1.2, textTransform: 'uppercase' }}>
            {t.title}
          </h2>
          <p style={{ color: '#666', fontSize: '13px', lineHeight: 1.7, margin: '0 0 32px' }}>
            {t.sub}
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="email"
              placeholder={t.placeholder}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{
                padding: '14px 16px', border: '1px solid #2a2a2a',
                backgroundColor: '#1a1a1a', color: '#fff', fontSize: '14px',
                outline: 'none', fontFamily: 'Arial'
              }}
            />
            {error && <p style={{ color: '#FF0066', fontSize: '12px', margin: 0 }}>{error}</p>}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                backgroundColor: loading ? '#888' : '#FF0066',
                color: '#fff', border: 'none', padding: '16px',
                fontSize: '11px', letterSpacing: '3px', fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}>
              {loading ? t.loading : t.btn}
            </motion.button>
          </form>

          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #1a1a1a', textAlign: 'center' }}>
            <p style={{ color: '#444', fontSize: '11px', margin: '0 0 10px' }}>
              {t.noRegister}
            </p>
            <span onClick={onSkip} style={{ color: '#555', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', letterSpacing: '0.5px' }}>
              {t.withWatermark}
            </span>
          </div>

          <p style={{ color: '#333', fontSize: '10px', margin: '16px 0 0', textAlign: 'center', lineHeight: 1.6 }}>
            {t.legal}
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}